import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { detect } from 'detect-browser'
import { useRouteMatch } from 'react-router-dom'

import isEmail from 'validator/lib/isEmail'

import {
  Loader,
  NoBgLoaderWrapper
} from '../../components/page-loader/styled-components'

import { FullContainer, InsideContainer } from './styled-components'

import { makeZendeskRedirectLink } from '../../utils/getZendeskLinks'

import { useAuthStore } from '../../zustand/authentication/use-auth-store'

import { useLoginMutation } from '../../react-query/authentication/mutations/use-login-mutation'

import { useLoginRedirectMutation } from '../../react-query/authentication/mutations/use-login-redirect-mutation'
import { useLogin2faMutation } from '../../react-query/authentication/mutations/use-login-2fa-mutation'
import { useLoginVerifyEmailMutation } from '../../react-query/authentication/mutations/use-login-verify-email-mutation'
import LoginStoreBanner from './components/login/login-store-banner'
import LoginLogo from './components/login/login-logo'
import LoginErrors from './components/login/login-errors'
import LoginForm from './components/login/login-form'
import Login2FAForm from './components/login/login-2fa-form'
import LoginFooterLinks from './components/login/login-footer-links'

const Login = ({ isInPopup, hideLogo }) => {
  const [emailMissing, setEmailMissing] = useState(false)
  const [passwordMissing, setPasswordMissing] = useState(false)
  const [twoFaCodeMissing, setTwoFaCodeMissing] = useState(false)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [twoFaCode, setTwoFaCode] = useState('')

  const query = new URLSearchParams(window.location?.search)

  const match = useRouteMatch()

  const params = match?.params

  const {
    error: errorLogin,
    mutateAsync: handleLoginHook,
    isPending: isLoadingLogin
  } = useLoginMutation()

  const {
    error: errorLogin2fa,
    mutateAsync: handleLogin2fa,
    isPending: isLoadingLogin2fa
  } = useLogin2faMutation()

  const {
    error: errorLoginRedirect,
    mutateAsync: handleLoginRedirect,
    isPending: isLoadingLoginRedirect
  } = useLoginRedirectMutation()

  const {
    data: emailVerified,
    isPending: emailVerifying,
    error: emailWrong,
    mutateAsync: handleVerifyEmail
  } = useLoginVerifyEmailMutation()

  const { redirectToken, userType, show2FA, setShow2FA } = useAuthStore()

  const loginError = errorLogin || errorLogin2fa || errorLoginRedirect

  const isLoading =
    isLoadingLogin || isLoadingLogin2fa || isLoadingLoginRedirect

  const returnTo = query.get('return_to')

  const browser = detect()

  const handleBackToLogin = () => {
    setShow2FA(undefined)
  }

  const handleSetEmail = ({ target: { value } }) => {
    setEmail(value)
    if (isEmail(value)) {
      setEmailMissing(false)
    }
  }

  const handleSetPassword = ({ target: { value } }) => {
    setPassword(value)
    if (value.length > 7) {
      setPasswordMissing(false)
    }
  }

  const handleResetPassword = () => {
    setPassword('')
    setTwoFaCode('')
  }

  const handleSetTwoFaCode = (e) => {
    const value = e.target.value.replace(/\D/g, '')

    setTwoFaCode(`${value}`)

    if (value) {
      setTwoFaCodeMissing(false)
    }
  }

  const handleLoginKeypress = async (e) => {
    if (e.key === 'Enter') {
      await handleLogin()
    }
  }

  const handleLogin = async () => {
    if (!show2FA) {
      if (email && isEmail(email) && password) {
        if (returnTo) {
          await handleLoginRedirect({ email, password })
        } else {
          await handleLoginHook({ email, password })
        }
      } else {
        if (!email || !isEmail(email)) {
          setEmailMissing(true)
          handleResetPassword()
        }
        if (!password) {
          setPasswordMissing(true)
        }
      }
    } else {
      setTwoFaCodeMissing(false)
      if (twoFaCode) {
        await handleLogin2fa({ email, password, twoFaCode })
      } else {
        setTwoFaCodeMissing(true)
      }
    }
  }

  useEffect(() => {
    if (params?.mode === 'verifyEmail' && params?.token) {
      handleVerifyEmail(params?.token)
    }

    if (userType === 'SAML') {
      setEmail('')
    }
  }, [])

  useEffect(() => {
    if (redirectToken) {
      window.location = makeZendeskRedirectLink(
        redirectToken.access_token,
        returnTo
      )
    }
  }, [redirectToken, returnTo])

  useEffect(() => {
    if (show2FA) {
      setTwoFaCodeMissing(false)
    } else {
      setEmailMissing(false)
      setPasswordMissing(false)
    }
  }, [loginError, show2FA])

  return (
    <FullContainer isInPopup={isInPopup}>
      {isLoading && (
        <NoBgLoaderWrapper className="noscroller">
          <Loader className="loader" />
        </NoBgLoaderWrapper>
      )}
      {!isLoading && (
        <span>
          <LoginStoreBanner browser={browser} />

          <InsideContainer
            isInPopup={isInPopup}
            maxWidth={400}
            onKeyPress={handleLoginKeypress}
          >
            <div>
              <LoginLogo hideLogo={hideLogo} isInPopup={isInPopup} />

              <LoginErrors
                browser={browser}
                params={params}
                loginError={loginError}
                emailWrong={emailWrong}
                emailVerified={emailVerified}
                emailVerifying={emailVerifying}
              />

              {!show2FA && !emailVerifying && (
                <LoginForm
                  email={email}
                  emailMissing={emailMissing}
                  password={password}
                  passwordMissing={passwordMissing}
                  handleSetEmail={handleSetEmail}
                  handleSetPassword={handleSetPassword}
                  emailVerifying={emailVerifying}
                  handleLogin={handleLogin}
                />
              )}
              {show2FA && (
                <Login2FAForm
                  email={email}
                  show2FA={show2FA}
                  twoFaCode={twoFaCode}
                  twoFaCodeMissing={twoFaCodeMissing}
                  handleSetTwoFaCode={handleSetTwoFaCode}
                  handleLogin={handleLogin}
                  handleBackToLogin={handleBackToLogin}
                  emailVerifying={emailVerifying}
                />
              )}
            </div>
            <LoginFooterLinks show2FA={show2FA} isInPopup={isInPopup} />
          </InsideContainer>
        </span>
      )}
    </FullContainer>
  )
}

Login.propTypes = {
  isInPopup: PropTypes.bool,
  hideLogo: PropTypes.bool
}

export default Login
